import {
  EMPTY_IDS,
  useSiteReportConfigurationState,
} from "react-migration/layouts/SiteReport/useSiteReportConfiguration";
import { useEffect, useMemo } from "react";

import { LayerTypePrintableProps } from "../../types";
import { PlanningApplication } from "react-migration/domains/planning/types";
import { PlanningApplicationPrintableTable } from "./PlanningApplicationPrintableTable";
import { ScopeType } from "src/js/stores/site/store";
import { Table } from "react-migration/components/TableDeprecated";
import { addBufferToGeometry } from "react-migration/domains/sites/helpers/addBufferToGeometry";
import { clsx } from "clsx";
import { max } from "lodash";
import { negativeBuffer } from "react-migration/layouts/map/Multilayer/geometry_utilities";
import { useGeometryTransformer } from "react-migration/layouts/SiteReport/hooks/useGeometryTransformer";
import { usePlanningApplicationsByGeometry } from "./SelectionSidebar/usePlanningApplicationsByGeometry";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

/** Planning Applications are identified via their address. */
function planningIdentifier<T extends { address: string }>(planningApplication: T) {
  return planningApplication.address;
}

export function PlanningApplicationPrintable({ selection, onLoaded, onUnmount }: LayerTypePrintableProps) {
  const { t } = useTranslation();
  const siteId = selection.id as string;
  const { scopeConfig } = useGeometryTransformer(siteId, "PLANNING_APPLICATIONS_ASSESSMENT", selection);

  const onSiteGeometry = useMemo(() => {
    const baseGeometry = selection.feature?.geometry;
    if (!baseGeometry) return null;

    return negativeBuffer(baseGeometry) ?? baseGeometry;
  }, [selection.feature?.geometry]);

  // Create independent geometry for nearby planning applications (always uses positive buffer)
  const nearbyGeometry = useMemo(() => {
    const baseGeometry = selection.feature?.geometry;
    if (!baseGeometry) return null;

    const radius = scopeConfig.radius ?? 100;
    return addBufferToGeometry(baseGeometry, radius)?.geometry ?? baseGeometry;
  }, [selection.feature?.geometry, scopeConfig.radius]);

  const { data: onSitePlanningApplications, loading } = usePlanningApplicationsByGeometry(onSiteGeometry);

  const { data: allPlanningApplications, loading: nearbyLoading } = usePlanningApplicationsByGeometry(
    scopeConfig.scopeType === ScopeType.NEARBY ? nearbyGeometry : null
  );

  // Deduplicate to get nearby-only planning applications (excluding on-site)
  const nearbyOnlyPlanningApplications = useMemo(() => {
    if (scopeConfig.scopeType !== ScopeType.NEARBY || !onSitePlanningApplications || !allPlanningApplications) {
      return [];
    }

    const onSiteAddresses = new Set(onSitePlanningApplications.map(planningIdentifier));
    return allPlanningApplications.filter((application) => !onSiteAddresses.has(planningIdentifier(application)));
  }, [scopeConfig.scopeType, onSitePlanningApplications, allPlanningApplications]);

  useEffect(() => {
    const isOnSiteLoaded = !loading;
    const isNearbyLoaded = scopeConfig.scopeType !== ScopeType.NEARBY || !nearbyLoading;

    if (isOnSiteLoaded && isNearbyLoaded) {
      onLoaded();
    }
  }, [loading, nearbyLoading, scopeConfig.scopeType, onLoaded]);

  useEffect(() => {
    return () => {
      onUnmount();
    };
  }, [onUnmount]);

  const planningIds = useMemo(
    () => onSitePlanningApplications?.map(planningIdentifier) ?? EMPTY_IDS,
    [onSitePlanningApplications]
  );

  const nearbyPlanningIds = useMemo(
    () => nearbyOnlyPlanningApplications?.map(planningIdentifier) ?? EMPTY_IDS,
    [nearbyOnlyPlanningApplications]
  );

  const { hiddenIds, allHidden, allVisible, toggleId, toggleAll } = useSiteReportConfigurationState(
    "excludePlanningApplicationIds",
    selection.id as string,
    planningIds
  );

  const {
    hiddenIds: nearbyHiddenIds,
    allHidden: nearbyAllHidden,
    allVisible: nearbyAllVisible,
    toggleId: nearbyToggleId,
    toggleAll: nearbyToggleAll,
  } = useSiteReportConfigurationState("excludePlanningApplicationIds", `${selection.id}_nearby`, nearbyPlanningIds);

  const shouldHideInPrint = scopeConfig.scopeType === ScopeType.NEARBY ? allHidden && nearbyAllHidden : allHidden;

  if (
    (scopeConfig.scopeType === ScopeType.ON_SITE && !onSitePlanningApplications?.length) ||
    (scopeConfig.scopeType === ScopeType.NEARBY &&
      !nearbyOnlyPlanningApplications.length &&
      !onSitePlanningApplications?.length)
  ) {
    return null;
  }

  return (
    <div className={clsx("mb-10 print:mt-4", { "print:hidden": shouldHideInPrint })} data-testid="planning-report">
      <h3 className="text-sm">{t("site_report.contextual_data.on_site")}</h3>
      <div className="mb-4">
        {t("sites.card.site_report.planning.estimated_maximum_dwellings_planned_for_site")}:{" "}
        {max(onSitePlanningApplications?.map((planningApplication) => planningApplication.num_dwellings))}
      </div>
      <div className="mb-1 italic text-neutral-500">
        {t("sites.card.site_report.planning.planning_applications_by_classification_and_distance")}
      </div>
      <div className="mb-4">
        <TableSummary planningApplications={onSitePlanningApplications ?? []} />
      </div>

      <PlanningApplicationPrintableTable
        planningApplications={onSitePlanningApplications}
        hiddenIds={hiddenIds}
        allVisible={allVisible}
        toggleId={toggleId}
        toggleAll={toggleAll}
        scopeConfig={scopeConfig}
        planningIdentifier={planningIdentifier}
      />

      {scopeConfig.scopeType === ScopeType.NEARBY && nearbyOnlyPlanningApplications.length > 0 && (
        <div className="mt-6">
          <PlanningApplicationPrintableTable
            planningApplications={nearbyOnlyPlanningApplications}
            hiddenIds={nearbyHiddenIds}
            allVisible={nearbyAllVisible}
            toggleId={nearbyToggleId}
            toggleAll={nearbyToggleAll}
            scopeConfig={scopeConfig}
            scopeType={ScopeType.NEARBY}
            planningIdentifier={planningIdentifier}
          />
        </div>
      )}
    </div>
  );
}

export const TableSummary = ({ planningApplications }: { planningApplications: PlanningApplication[] }) => {
  const { t } = useTranslation();
  const groupedPlanningApplications = classifications?.map((classification) => {
    const onSite = planningApplications?.filter(
      (planningApplication) => planningApplication.classification === classification.id
    );

    return {
      classification: classification.display_name,
      onSite: onSite.length,
    };
  });

  return (
    <Table
      size="small"
      primaryKey="classification"
      columns={[
        {
          label: t("sites.card.site_report.planning.classification"),
          render: (row) => toTitleCase(row.classification.replace("_", " ")),
        },
        {
          label: t("sites.card.site_report.planning.within_site"),
          rowKey: ScopeType.ON_SITE,
        },
      ]}
      rows={groupedPlanningApplications}
    />
  );
};

const classifications = [
  { id: "RESIDENTIAL", display_name: "Residential" },
  { id: "COMMERCIAL", display_name: "Commercial" },
  { id: "MIXED_USE", display_name: "Mixed Use" },
  { id: "OTHER", display_name: "Other" },
  { id: "UNKNOWN", display_name: "Unknown" },
];

export const toTitleCase = (str: string) => {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase();
  });
};
